import { createApiClient } from '../../../../api/apiClient';

type FilterParamValue = string | number | Array<string | number>;

// ─── Response Types ───────────────────────────────────────────────────────────

export interface KpiMetric {
  percentage?: number;
  numeratorValue?: number;
  numeratorLabel: string;
  denominatorValue: number;
  denominatorLabel: string;
  subLine: string;
  format: string;
}

export interface RouteOutcomes {
  approved: number;
  approvedPercentage: number;
  pending: number;
  pendingPercentage: number;
  declined: number;
  declinedPercentage: number;
}

export interface TrendDataPoint {
  label: string;
  subLabel?: string;
  year?: number;
  period?: number;
  week?: number;
  approvedRoutes: number;
  targetRoutes: number;
  percentage: number;
  highlighted?: boolean;
  isHighlighted?: boolean;
}

export interface TrendData {
  level: 'year' | 'period' | 'week' | 'day';
  chartSubtitle: string;
  dataPoints: TrendDataPoint[];
}

export interface BreakdownBar {
  label: string;
  approvedRoutes: number;
  targetRoutes: number;
  percentage: number;
  belowTarget?: boolean;
  isBelowTarget?: boolean;
}

export interface BreakdownData {
  level: 'region' | 'market' | 'location';
  title: string;
  bars: BreakdownBar[];
}

export interface PerfectTruckKpiResponse {
  perfectTruckExecution: KpiMetric;
  certifierExecution: KpiMetric;
  checkerExecution: KpiMetric;
  totals: {
    totalRoutes: number;
    approvedRoutes: number;
    createdRoutes: number;
    checkedRoutes: number;
    targetRoutes: number;
  };
  routeOutcomes: RouteOutcomes;
  trend: TrendData;
  breakdown: BreakdownData;
  executionTimeMs: number;
  message: string;
}

// ─── Request Params ───────────────────────────────────────────────────────────

export interface PerfectTruckKpiParams {
  region?: FilterParamValue;
  market?: FilterParamValue;
  location?: FilterParamValue;
  year?: FilterParamValue;
  period?: FilterParamValue;
  week?: FilterParamValue;
  routeType?: FilterParamValue;
  locationHierarchy?: 'region' | 'market' | 'location';
  timeframe?: 'year' | 'period' | 'week' | 'day';
  /** Delivery Date range filter — ISO (yyyy-mm-dd) bounds, inclusive */
  startDate?: string;
  endDate?: string;
}

const splitFilterValues = (value?: FilterParamValue): string[] => {
  if (value == null) return [];
  const rawValues = Array.isArray(value) ? value : [value];
  return rawValues
    .flatMap((entry) => String(entry).split(','))
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
};

const toCsv = (value?: FilterParamValue): string | undefined => {
  const parts = splitFilterValues(value);
  return parts.length > 0 ? parts.join(',') : undefined;
};

const toNumberArray = (value?: FilterParamValue): number[] =>
  splitFilterValues(value)
    .map((entry) => Number(entry.replace(/^P/i, '').replace(/^W/i, '')))
    .filter((n) => Number.isFinite(n));

const toStringArray = (value?: FilterParamValue): string[] => splitFilterValues(value);

const buildKpiGetParams = (params: PerfectTruckKpiParams): Record<string, string> => {
  const query: Record<string, string> = {};

  const region = toCsv(params.region);
  const market = toCsv(params.market);
  const location = toCsv(params.location);
  const year = toCsv(params.year);
  const period = toCsv(params.period);
  const week = toCsv(params.week);
  const routeType = toCsv(params.routeType);

  if (region) query.region = region;
  if (market) query.market = market;
  if (location) query.location = location;
  if (year) query.year = year;
  if (period) query.period = period;
  if (week) query.week = week;
  if (routeType) query.routeType = routeType;
  if (params.locationHierarchy) query.locationHierarchy = params.locationHierarchy;
  if (params.timeframe) query.timeframe = params.timeframe;
  if (params.startDate) query.startDate = params.startDate;
  if (params.endDate) query.endDate = params.endDate;

  return query;
};

const buildKpiPostPayload = (params: PerfectTruckKpiParams): Record<string, any> => ({
  region: toStringArray(params.region),
  market: toStringArray(params.market),
  location: toStringArray(params.location),
  year: toNumberArray(params.year),
  period: toNumberArray(params.period),
  week: toNumberArray(params.week),
  routeType: toStringArray(params.routeType),
  ...(params.locationHierarchy ? { locationHierarchy: params.locationHierarchy } : {}),
  ...(params.timeframe ? { timeframe: params.timeframe } : {}),
  ...(params.startDate ? { startDate: params.startDate } : {}),
  ...(params.endDate ? { endDate: params.endDate } : {}),
});

const shouldUsePostForKpi = (params: PerfectTruckKpiParams): boolean => {
  const locationCount = toStringArray(params.location).length;
  if (locationCount > 20) return true;

  const queryString = new URLSearchParams(buildKpiGetParams(params)).toString();
  return queryString.length > 1500;
};

const normalizeKpiResponse = (data: any): PerfectTruckKpiResponse => {
  const trend = data?.trend ?? {};
  const breakdown = data?.breakdown ?? {};

  return {
    ...data,
    trend: {
      ...trend,
      level: (trend.level ?? 'period') as PerfectTruckKpiResponse['trend']['level'],
      dataPoints: (trend.dataPoints ?? []).map((point: any) => ({
        ...point,
        highlighted: point?.isHighlighted ?? point?.highlighted ?? false,
        isHighlighted: point?.isHighlighted ?? point?.highlighted ?? false,
      })),
    },
    breakdown: {
      ...breakdown,
      bars: (breakdown.bars ?? []).map((bar: any) => ({
        ...bar,
        belowTarget: bar?.isBelowTarget ?? bar?.belowTarget ?? false,
        isBelowTarget: bar?.isBelowTarget ?? bar?.belowTarget ?? false,
      })),
    },
  };
};

// ─── KPI summary and charts API (GET for normal size, POST for large filters) ─

export const fetchPerfectTruckKpi = async (
  params: PerfectTruckKpiParams = {},
): Promise<PerfectTruckKpiResponse> => {
  const api = createApiClient();

  if (shouldUsePostForKpi(params)) {
    const res = await api.post<PerfectTruckKpiResponse>(
      '/perfect-truck-kpi',
      buildKpiPostPayload(params),
    );
    return normalizeKpiResponse(res.data);
  }

  const res = await api.get<PerfectTruckKpiResponse>('/perfect-truck-kpi', {
    params: buildKpiGetParams(params),
  });
  return normalizeKpiResponse(res.data);
};

// ─── Location Details Types ───────────────────────────────────────────────────

export interface LocationTimeframeBreakdown {
  label: string;
  subLabel?: string;
  year: number;
  created: number;
  target: number;
  approved: number;
  pending: number;
  declined: number;
  execution: number;
  isHighlighted: boolean;
}

export interface LocationDetailRecord {
  region: string;
  market: string;
  location: string;
  created: number;
  target: number;
  ptExecution: number;
  approved: number;
  pending: number;
  declined: number;
  timeframeBreakdown: LocationTimeframeBreakdown[];
}

export interface LocationDetailsResponse {
  data: LocationDetailRecord[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
}

export interface LocationDetailsParams extends PerfectTruckKpiParams {
  regionUnit?: FilterParamValue;
  marketPod?: FilterParamValue;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

// ─── Refresh Status ───────────────────────────────────────────────────────────

export interface RefreshStatusResponse {
  lastRefreshedLabel: string;
}

export const fetchRefreshStatus = async (): Promise<RefreshStatusResponse> => {
  const api = createApiClient();
  const res = await api.get<RefreshStatusResponse>(
    '/perfect-truck-kpi/refresh-status',
  );
  return res.data;
};

// ─── Location Details API call (using POST to handle large location filters) ────

export const fetchLocationDetails = async (
  params: LocationDetailsParams = {},
): Promise<LocationDetailsResponse> => {
  const api = createApiClient();

  // Build request body from params.
  // Keep region/market for backward compatibility while also sending
  // regionUnit/marketPod to match the documented backend contract.
  const requestBody: Record<string, any> = {};

  const regionCsv = toCsv(params.regionUnit ?? params.region);
  const marketCsv = toCsv(params.marketPod ?? params.market);
  const locationCsv = toCsv(params.location);
  const yearCsv = toCsv(params.year);
  const periodCsv = toCsv(params.period);
  const weekCsv = toCsv(params.week);
  const routeTypeCsv = toCsv(params.routeType);

  if (regionCsv) {
    requestBody.region = regionCsv;
    requestBody.regionUnit = regionCsv;
  }
  if (marketCsv) {
    requestBody.market = marketCsv;
    requestBody.marketPod = marketCsv;
  }
  if (locationCsv) requestBody.location = locationCsv;
  if (yearCsv) requestBody.year = yearCsv;
  if (periodCsv) requestBody.period = periodCsv;
  if (weekCsv) requestBody.week = weekCsv;
  if (routeTypeCsv) requestBody.routeType = routeTypeCsv;
  if (params.locationHierarchy) requestBody.locationHierarchy = params.locationHierarchy;
  if (params.timeframe) requestBody.timeframe = params.timeframe;
  if (params.startDate) requestBody.startDate = params.startDate;
  if (params.endDate) requestBody.endDate = params.endDate;
  requestBody.page = params.page ?? 0;
  requestBody.size = params.size ?? 10;
  if (params.sortBy) requestBody.sortBy = params.sortBy;
  if (params.sortDirection) requestBody.sortDirection = params.sortDirection;

  // Use POST to handle large location filters
  const res = await api.post<LocationDetailsResponse>(
    '/perfect-truck-kpi/location-details',
    requestBody,
  );
  return res.data;
};

// ─── Route Types (with active status, scoped to this dashboard) ─────────────
// Intentionally self-contained here rather than reusing Admin's
// routeManagementApi.fetchAllRouteTypes — that function is shared by other
// Route Management flows, so it's left untouched to avoid affecting them.

export interface RouteTypeOption {
  label: string;
  value: string;
  isActive: boolean;
}

export const fetchAllRouteTypesWithStatus = async (): Promise<RouteTypeOption[]> => {
  const api = createApiClient();
  const res = await api.get<{ data: Array<{ routeType: string; isActive?: boolean }> }>(
    '/perfect-truck/config/route-types',
  );
  return res.data.data.map((rt) => ({
    label: rt.routeType,
    value: rt.routeType,
    isActive: rt.isActive !== false,
  }));
};
